import React, { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
  useFetchQrCodeByGenerationIDMutMutation,
} from "../../../features/tenant/qrCode/qrCodeApi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useLocation, useNavigate } from "react-router-dom";
import ViewQr from "../../../components/Tenant/ViewQR/ViewQr";
import QRCode from "react-qr-code";
import { getVendorName } from "../../../utils/getVendorName";
import { toast } from "react-hot-toast";
import { Menu } from "@headlessui/react";
import { HiPrinter, HiOutlinePrinter } from "react-icons/hi";
import { FaPrint } from "react-icons/fa";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import {
  useFetchAllPrintersMutation,
  usePrintQrCodesMutation,
} from "../../../features/tenant/qrPrinter/qrPrinterApi";
import {
  useFetchTemplateMappingByCategoryIdMutation,
  useFetchAllTemplateMappingsMutation,
} from "../../../features/tenant/qrPrinter/printerTemplateMappingApi";
import { useFetchProductPrinterSettingsMutation } from "../../../features/tenant/products/productsApi";
import axios from "axios";
import * as XLSX from "xlsx";
import ViewBarcode from "../../../components/Tenant/ViewQR/ViewBarcode";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import TenantSlugs from "../../../utils/TenantSlug";

const QrByBatch = () => {

  const tenantId = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
  const defaultLimit = tenantId == TenantSlugs.seedworks ? 500 : 10;
  const paginationPerPage = defaultLimit;
  const navigate = useNavigate();

  // ========== HELPER FUNCTIONS ==========

  // Calculate total meters (qty * size)
  const calculateTotalMeters = (qty, sizeValue) => {
    if (!qty || !sizeValue) return "30.50m";
    try {
      const total = parseFloat(qty) * parseFloat(sizeValue);
      return `${total.toFixed(2)}m`;
    } catch (e) {
      console.error("Error calculating total meters:", e);
      return "30.50m";
    }
  };

  // Calculate total feet (qty * size * 3.28084)
  const calculateTotalFeet = (qty, sizeValue) => {
    if (!qty || !sizeValue) return "100.071 Rft";
    try {
      const meters = parseFloat(qty) * parseFloat(sizeValue);
      const feet = meters * 3.28084;
      return `${feet.toFixed(3)} Rft`;
    } catch (e) {
      console.error("Error calculating total feet:", e);
      return "100.071 Rft";
    }
  };

  // Generate serial number from scratch_code
  const generateSerialNumber = (scratchCode) => {
    if (!scratchCode) return "3008821";

    try {
      // If scratch code is numeric, use it directly
      const numericCode = scratchCode.replace(/\D/g, '');

      // Pad to 7 digits if needed
      if (numericCode.length < 7) {
        return numericCode.padStart(7, '0');
      } else if (numericCode.length > 7) {
        return numericCode.substring(0, 7);
      }
      return numericCode;
    } catch (e) {
      console.error("Error generating serial number:", e);
      return "3008821";
    }
  };

  // Helper function to build complete label data from row
  const buildLabelData = (row, formOverrides = {}) => {
    // Parse size to extract numeric value
    const sizeValue = row.size ? row.size.split(' ')[0] : "3.05";
    const sizeUnit = row.size ? row.size.split(' ')[1] || "Mtr" : "Mtr";

    const labelObj = {
      // Core identification
      unique_code: row.unique_code,
      batch_running_code: row.batch_running_code || row.id.toString().padStart(7, '0'),

      // Label top section
      product_color: row.color || "Walnut",
      product_name: row.product_name || "J-Trim",
      product_code: row.product_code || "SVP-15-NU",

      // Quantity section
      quantity_per_box: row.qty ? `${row.qty} NOS` : "10 NOS",
      total_length_meters: calculateTotalMeters(row.qty, sizeValue),
      total_length_feet: calculateTotalFeet(row.qty, sizeValue),
      size_per_piece: sizeValue,
      size_unit: sizeUnit,
      q_code: "Q:08",
      packer_code: "Packer:09",

      // Pricing section
      mrp: row.mrp || "7805.50",
      mfg_date: new Date().toLocaleDateString('en-GB').replace(/\//g, '.'),

      // Weight section (static)
      net_weight: "4.22",
      gross_weight: "4.37",

      // Batch section
      batch_no: row.batch_code || "4N/PT WE01",

      // Bottom section
      fragile_text: "FRAGILE",
      ldp_text: "LDPE",
      serial_number: generateSerialNumber(row.scratch_code),
      barcode: '',

      // Company info (from your image)
      company_name: "VOX Interior & Exterior Solutions Pvt Ltd",
      company_address_line1: "Plot #78/A, KIADB Industrial area",
      company_address_line2: "1st Phase, Jigani, Anekal taluk,",
      company_address_line3: "Bangalore-560105",
      joint_venture: "VOX Profile Poland.",
      consumer_phone: "080 43741810",
      consumer_email: "customercare@voxindia.co",
      website: "www.voxindia.co",

      // Additional product info for reference
      brand_name: row.brand_name || "VOX",
      description: row.description || "",
      category_name: row.category_name || "",
      classification: row.classification || "",
      scratch_code: row.scratch_code || "",
      generation_id: row.generation_id,
      created_at: row.created_at,
      created_by_name: row.created_by_name,
      qty: row.qty || 10,
    };

    // Apply VOX print form overrides
    if (formOverrides.pieces)      labelObj.quantity_per_box = formOverrides.pieces;
    if (formOverrides.rft)         labelObj.rft              = formOverrides.rftUnit ? `${formOverrides.rft} ${formOverrides.rftUnit}` : formOverrides.rft;
    if (formOverrides.lengthVal)   labelObj.length_val       = formOverrides.lengthUnit ? `${formOverrides.lengthVal} ${formOverrides.lengthUnit}` : formOverrides.lengthVal;
    if (formOverrides.length)      labelObj.length           = formOverrides.length;
    if (formOverrides.netWeight)   labelObj.net_weight       = formOverrides.netWeight;
    if (formOverrides.grossWeight) labelObj.gross_weight     = formOverrides.grossWeight;
    if (formOverrides.qCode)       labelObj.q_code           = formOverrides.qCode;
    if (formOverrides.packerCode)  labelObj.packer_code      = formOverrides.packerCode;
    if (formOverrides.batchNo)     labelObj.batch_no         = formOverrides.batchNo;
    if (formOverrides.nominalQty)     labelObj.nominal_qty    = formOverrides.nominalQty;
    if (formOverrides.nominalWidth)   labelObj.nominal_width  = formOverrides.nominalWidth;
    if (formOverrides.nominalHeight)  labelObj.nominal_height = formOverrides.nominalHeight;

    return labelObj;
  };

  // Main DataTable Columns
  const columns = [
    {
      name: "Printed",
      cell: (row) =>
        row.is_printed == true ? (
          <span className="text-green-600">Yes</span>
        ) : (
          <span className="text-red-600">No</span>
        ),
      sortable: true,
      width: "90px",
    },
    {
      name: "SNo",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "100px",
    },
    {
      name: "Batch Running / Serial Code",
      cell: (row) => row.batch_running_code,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Product Code",
      cell: (row) => row.product_code,
      sortable: true,
      width: "130px",
    },
    {
      name: "Qr Code",
      cell: (row) => row.unique_code,
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Scratch Code",
      cell: (row) => row.scratch_code,
      sortable: true,
    },
    {
      name: "Type",
      cell: (row) => (
        <span
          className={`text-sm p-1 capitalize rounded-sm  font-bold bg-${row.parent_code === null
            ? "gray"
            : row.parent_code === "false" || row.qr_type == '2'
              ? "red"
              : "blue"
            }-500 text-white animate-pulse duration-1000`}
        >
          {row.parent_code === null || row.qr_type == '1'
            ? "Child"
            : row.parent_code === "false" || row.qr_type == '2'
              ? "Parent"
              : "Invalid"}
        </span>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "MRP",
      cell: (row) => row.mrp,
      sortable: true,
      width: "100px",
    },
    {
      name: "Qr Status",
      cell: (row) => (
        <span
          className={`text-sm p-1 capitalize rounded-sm  font-bold bg-${row.qr_status == "2" ? "red" : "blue"
            }-700 text-white animate-pulse duration-0`}
        >
          {row.qr_status == "2" ? "Scanned" : "Not Scanned"}
        </span>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Created At",
      cell: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
    },
    // ========== PRINT LABEL BUTTON ==========
    {
      name: "Print Label",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-green-600 px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-green-700 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-green-700 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-green-800 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)]"
            onClick={() => handlePrintSingleLabel(row)}
            disabled={printLabelLoading}
            title="Print Product Label"
          >
            <HiOutlinePrinter className="mr-1" />
            {printLabelLoading && currentPrintingId === row.id ? "Printing..." : "Label"}
          </button>
        </div>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: "",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-red-600 px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white animate-blinking-button shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-red-700 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-red-700 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-red-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
            onClick={() =>
              navigate(`/genefied/${getVendorName()}/tracking`, { state: row })
            }
          >
            Track
          </button>
        </div>
      ),
      sortable: true,
    },
    {
      name: "",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] "
            onClick={() => handleShowQr(row)}
          >
            GVerify Qr
          </button>
        </div>
      ),
      sortable: true,
    },
    {
      name: "",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] "
            onClick={() => handleWebGenefiedQr(row)}
          >
            Web Qr
          </button>
        </div>
      ),
      sortable: true,
    },
    {
      name: "",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] "
            onClick={() => handleBatchShowQr(row)}
          >
            View Qr
          </button>
        </div>
      ),
      sortable: true,
    },
    {
      name: "",
      cell: (row) => (
        <div>
          <button
            className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] "
            onClick={() => handleShowBar(row)}
          >
            View Bar
          </button>
        </div>
      ),
      sortable: true,
    },
  ];


  const [myList, setMyList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [limit, setLimit] = useState(defaultLimit);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);

  const [childOrParent, setChildOrParent] = useState("all");

  const [generationId, setGenerationId] = useState(null);

  const [startBatchRunningCode, setStartBatchRunningCode] = useState(null);
  const [endBatchRunningCode, setEndBatchRunningCode] = useState(null);
  const location = useLocation();

  // ========== LABEL PRINTING STATES ==========
  const [printLabelLoading, setPrintLabelLoading] = useState(false);
  const [currentPrintingId, setCurrentPrintingId] = useState(null);
  const [labelPrintModal, setLabelPrintModal] = useState(false);
  const [selectedForLabelPrint, setSelectedForLabelPrint] = useState([]);
  const [batchLabelPrinting, setBatchLabelPrinting] = useState(false);

  // ========== VOX PRINT FORM STATES ==========
  const [showPrintForm, setShowPrintForm] = useState(false);
  const [printFormRow, setPrintFormRow] = useState(null);
  const [printFormIsBatch, setPrintFormIsBatch] = useState(false);
  const [printFormData, setPrintFormData] = useState({
    pieces: '', rft: '', rftUnit: 'meter', lengthVal: '', lengthUnit: 'sqm', length: '',
    qCode: '', packerCode: '',
    nominalQty: '', nominalWidth: '', nominalHeight: '', netWeight: '', grossWeight: '',
    barcodeNumber: '',
    // Linerio-specific fields
    linProductName: '', linProductCode: '', linMrp: '',
    linOpr: '', linQ: '', linPacker: '',
    linSizeWidth: '', linSizeHeight: '',
    linCoverage: '', linCoverageUnit: 'Sqft', linPieces: '',
    linNetWeight: '', linGrossWeight: '', linBarcode: '',
  });
  const isVoxUrl = window.location.href.toLowerCase().includes('vox');
  const [selectedTemplate, setSelectedTemplate] = useState("vox_label.prn");
  const [pendingPrintBody, setPendingPrintBody] = useState(null);
  const [batchNumber, setBatchNumber] = useState("");
  // Captures form values so handleSendToPrinter can merge them into index.php variables
  const voxFormDataRef = useRef(printFormData);
  useEffect(() => {
    voxFormDataRef.current = printFormData;
  }, [printFormData]);

  const [fetchPrinterSettings] = useFetchProductPrinterSettingsMutation();

  // ========== TEMPLATE SELECTION STATES ==========
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [pendingGreenPrintRows, setPendingGreenPrintRows] = useState([]);

  // ========== GREEN PRINT FORM STATES ==========
  const [showGreenPrintForm, setShowGreenPrintForm] = useState(false);
  const [greenPrintFormData, setGreenPrintFormData] = useState({
    pieces: '',
    length: '',
    rft: '',
    qCode: '',
    packerCode: '',
    batchNo: '',
  });
  const [pendingGreenTemplate, setPendingGreenTemplate] = useState(null);

  const [
    fetchTemplateByCategoryId,
    { data: templateByCatData, error: templateByCatError },
  ] = useFetchTemplateMappingByCategoryIdMutation();

  const [
    fetchAllTemplates,
    { data: allTemplatesData, error: allTemplatesError },
  ] = useFetchAllTemplateMappingsMutation();

  // Load all templates on mount for the dropdown
  useEffect(() => {
    fetchAllTemplates({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
    });
  }, []);

  useEffect(() => {
    if (allTemplatesData && allTemplatesData.body) {
      setAllTemplates(allTemplatesData.body.data || []);
    }
  }, [allTemplatesData]);

  // When template by category is fetched, open the modal
  useEffect(() => {
    if (templateByCatData && templateByCatData.body) {
      const templates = templateByCatData.body.data || [];
      if (templates.length > 0) {
        setSelectedTemplate(templates[0]);
      } else {
        setSelectedTemplate(null);
      }
      setShowTemplateModal(true);
    }
    if (templateByCatError) {
      // No template found for category, still show modal with all templates
      setSelectedTemplate(null);
      setShowTemplateModal(true);
    }
  }, [templateByCatData, templateByCatError]);

  // ========== PHILIPS PHOENIX LABEL PRINTER CONFIG ==========
  const LABEL_PRINTER_CONFIG = {
    // Update this with your actual PHP server URL
    phpEndpoint: "http://localhost:8000/index.php", // Your PHP endpoint
    timeout: 30000, // 30 seconds timeout
  };

  useEffect(() => {
    const Id = location.state.id;
    setGenerationId(Id);
  }, [location.pathname]);

  const [
    qrListDataFun,
    {
      data: qrListData,
      isLoading: qrListDataIsLoading,
      error: qrListDataError,
    },
  ] = useFetchQrCodeByGenerationIDMutMutation();

  const [
    qrLiCsvDataFun,
    {
      data: qrLiCsvData,
      isLoading: qrLiCsvDataIsLoading,
      error: qrLiCsvDataError,
    },
  ] = useFetchQrCodeByGenerationIDMutMutation();

  useEffect(() => {
    if (qrLiCsvDataError) {
      console.log("qrLiCsvDataError", qrLiCsvDataError);
      toast.error("Something went wrong", { id: "qrLiCsvDataError" });
    }
  }, [qrLiCsvDataError]);

  const qrListDownLoad = () => {
    qrLiCsvDataFun({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      generationId: generationId,
    });
  };

  const fetchQRCodeList = () => {
    qrListDataFun({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      generationId: generationId,
      limit: limit,
      offset: offset,
    });
  };

  const generateQRCodePDF = () => {
    if (qrLiCsvData?.body?.qr?.length > 0) {
      const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
      const printWindow = window.open("", "_blank");

      if (printWindow) {
        printWindow.document.open();

        let pdfContent = `
          <html>
            <head>
              <title>QR Code PDF</title>
              <style>
                .qr-container {
                  display: flex;
                  flex-wrap: wrap;
                }
                .qr-code {
                  margin: 10px;
                }
                .qr-data {
                  margin: auto;
                  justify-content: center;
                  text-align: center;
                  font-size:12px;
                }
              </style>
            </head>
            <body>
              <h1>Qr Codes</h1>
              <div class="qr-container">`;

        qrLiCsvData.body.qr.forEach((data, index) => {
          const qrCodeImage = ReactDOMServer.renderToStaticMarkup(
            <QRCode
              value={`${process.env.REACT_APP_QR_URL}genefied/verifyQr?qrCode=${data.unique_code}`}
              size={64}
            />
          );

          pdfContent += `
          <div>
            <div class="qr-code"> 
              ${qrCodeImage}
            </div>
            <p class="qr-data " >
            ${data.batch_running_code}
            </p>
          </div>
          `;
        });

        pdfContent += `
              </div>
            </body>
          </html>
        `;

        printWindow.document.write(pdfContent);
        printWindow.document.close();

        printWindow.print();
        printWindow.close();
      }
    }
  };

  useEffect(() => {
    if (qrLiCsvDataIsLoading) {
      // toast.dismiss();
      // toast.loading("Fetching list ...", {
      //   id: "delete loading ",
      // });
    }
  }, [qrLiCsvDataIsLoading, qrLiCsvData]);

  useEffect(() => {
    if (generationId) {
      fetchQRCodeList();
    }
  }, [generationId, limit, offset]);

  useEffect(() => {
    if (generationId) {
      qrListDownLoad();
    }
  }, [generationId]);

  useEffect(() => {
    if (qrListData && qrListData.body) {
      toast.dismiss();
      if (qrListData.body.length == 0) {
        toast("No Qrs found ⚠️", { id: "fetchNoQrs" });
      } else if (qrListData.body.qr) {
        setData(qrListData.body.qr);
        setTotalRows(
          qrListData && qrListData.body.total ? qrListData.body.total : 0
        );
        // VOX: Pre-select template name from DB
        if (isVoxUrl && qrListData.body.qr.length > 0) {
          const dbTemplate = qrListData.body.qr[0].tempelate_name;
          if (dbTemplate) {
            setSelectedTemplate(dbTemplate);
          }
        }
      }
    }

    if (qrListDataError) {
      toast.dismiss();
      console.log("qr list error batch ", qrListDataError);
      toast.error("Some error occurred", { id: "fetchQrsError" });
    }

    if (qrListDataIsLoading) {
      toast.dismiss();
      toast.loading("Fetching qrs...", { id: "fetchingQrs" });
    }
  }, [qrListData, qrListDataIsLoading, qrListDataError]);

  const [showQr, setShowQr] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [qrData, setQrData] = useState(null);

  const handleShowQr = (data) => {
    console.log(data);
    const obj = {
      link: `http://gverify.me/?${data.unique_code}`,
      ...data,
    };

    setQrData(obj);
    setShowQr(true);
  };

  const handleBatchShowQr = (data) => {
    let obj = {
      link: `${data.unique_code}`,
      ...data,
    };

    // Ultimatrue Specific Changes
    if (JSON.parse(sessionStorage.getItem("vendorData")).tenant_id == 17) {
      obj.link = `${process.env.REACT_APP_QR_URL}genefied/verifyQr?qrCode=${data.unique_code}`;
    }

    setQrData(obj);
    setShowQr(true);
  };

  const handleWebGenefiedQr = (data) => {
    let obj = {
      link: `${process.env.REACT_APP_QR_URL}genefied/verifyQr?qrCode=${data.unique_code}`,
      ...data,
    };

    setQrData(obj);
    setShowQr(true);
  };

  const handleShowBar = (data) => {
    console.log(data);
    const obj = {
      link: `${data.unique_code}`,
      ...data,
    };

    setQrData(obj);
    setShowBar(true);
  };

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
  };

  const [selectedRows, setSelectedRows] = React.useState([]);
  const handleRowSelected = React.useCallback((state) => {
    if (printAllCheckBox) {
      setPrintAllCheckBox(false);
    }
    setSelectedRows(state.selectedRows);
  }, []);

  const [fetchAllPrinters, { data: printerListData, error: printerListError }] =
    useFetchAllPrintersMutation();

  useEffect(() => {
    fetchAllPrinters({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
    });
  }, []);

  const [
    printQrCodeFunc,
    {
      data: printQrCodeData,
      error: printQrCodeError,
      isLoading: printQrCodeisLoading,
    },
  ] = usePrintQrCodesMutation();
  const confirmInitialData = { open: false, freezed: 0, total: 0, printed: 0, printing: 0, dataToSend: null };
  const [confirmModal, setConfirmModal] = useState(confirmInitialData);

  useEffect(() => {
    if (printQrCodeData && printQrCodeData.body) {
      toast.dismiss();
      //Tibcon Spefic Changes
      if (JSON.parse(sessionStorage.getItem("vendorData")).tenant_id == 15) {
        const effectivePrintedCount = printQrCodeData.body.printed_count;
          if (effectivePrintedCount > 0) {
            toast.error(`Qrs already printed.`, {
              id: "alreadyPrintQrCodeData",
            });
          } else {
            toast.success("Prinitng " + printQrCodeData.body.total_qrs + " Qrs excluding freezed", {
              id: "printQrCodeData",
            });

            // VOX: Use tempelate_name from backend if available, else use selectedTemplate
            const dataToPrint = { ...printQrCodeData.body.data };
            if (isVoxUrl) {
              const dbTemplate = printQrCodeData.body.data?.tempelate_name;
              dataToPrint.filename = dbTemplate || selectedTemplate;
            }
            handleSendToPrinter(dataToPrint);
          }
          return;
        } else if (printQrCodeData.body.printed_count > 0) {
          setConfirmModal({ open: true, total: printQrCodeData.body.total_qrs, freezed: printQrCodeData.body.freeze_count, printed: printQrCodeData.body.printed_count, printing: printQrCodeData.body.to_print_count, dataToSend: printQrCodeData.body.data });
          return;
        }
        console.log("printQrCodeData", printQrCodeData);
        toast.success("Prinitng " + printQrCodeData.body.total_qrs + " Qrs excluding freezed", {
          id: "printQrCodeData",
        });

        // VOX: Use tempelate_name from backend if available, else use selectedTemplate
        const dataToPrint = { ...printQrCodeData.body.data };
        if (isVoxUrl) {
          const dbTemplate = printQrCodeData.body.data?.tempelate_name;
          dataToPrint.filename = dbTemplate || selectedTemplate;
        }
        handleSendToPrinter(dataToPrint);
      }

    if (printQrCodeError) {
      toast.dismiss();
      console.log("printQrCodeError", printQrCodeError);
      toast.error("Something went wrong", { id: "printQrCodeError" });
    }

    if (printQrCodeisLoading) {
      toast.loading("Please wait..");
    }
  }, [printQrCodeData, printQrCodeError]);

  const [printAllCheckBox, setPrintAllCheckBox] = useState(false);

  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme
    .color_shades;

  const [showPrinterModal, setShowPrinterModal] = useState(false);

  const handlePrintAll = () => {
    if (printAllCheckBox) {
      setPrintAllCheckBox(!printAllCheckBox);
      setSelectedRows([]);
    } else {
      setPrintAllCheckBox(!printAllCheckBox);
      setSelectedRows([{ type: "all" }]);
    }
  };

  const [selectedPrinter, setSelectedPrinter] = useState(null);

  const handleSubmit = async () => {
    if (selectedRows.length > 0) {
      console.log("selectedPrinter", selectedPrinter);

      //print all in batch
      if (selectedRows[0]?.type) {
        const body = {
          printer: selectedPrinter,
          type: "2",
          generation_id: location.state.id,
          childOrParent: childOrParent,
          batchNumber: batchNumber || undefined,
        };

        // Inform and request to skip freezed QRs when printing all
        const freezedOnPage = Array.isArray(data)
          ? data.filter((row) => row.is_freezed === true || row.is_freezed === "true").length
          : 0;
        if (freezedOnPage > 0) {
          toast("Freezed QR(s) will be skipped during printing.");
        }
        body.exclude_freezed = true;

        if (startBatchRunningCode && endBatchRunningCode) {
          body.startBatchRunningCode = startBatchRunningCode;
          body.endBatchRunningCode = endBatchRunningCode;
        }

        // VOX: Show print form first to collect label details before backend call
        if (isVoxUrl) {
          const sourceRow = Array.isArray(data) && data.length > 0 ? data[0] : {};
          // Strip trailing dimension from product name e.g. "SP LINERIO S-LINE WHITE 3,05Mx16" → "SP LINERIO S-LINE WHITE"
          const rawName = sourceRow.product_name || sourceRow.name || '';
          const linProductName = rawName.replace(/\s+\d[\d.,]*[Mm][xX×]\d+\s*$/, '').trim();
          setPendingPrintBody(body);
          setPrintFormIsBatch(true);
          
          let defaultSettings = {
            pieces: sourceRow.qty ? `${sourceRow.qty}` : '10', rft: '', rftUnit: 'meter',
            lengthVal: '', lengthUnit: 'sqm', length: '',
            qCode: 'Q:08', packerCode: 'Packer:09',
            netWeight: '', grossWeight: '',
            nominalQty: '', nominalWidth: '', nominalHeight: '',
            productCode: sourceRow.product_code || '',
            productColor: sourceRow.color || '',
            productCategoryName: sourceRow.product_category_name || sourceRow.classification || '',
            categoryName: sourceRow.category_name || '',
            mrp: sourceRow.product_mrp || sourceRow.mrp || '',
            barcodeNumber: sourceRow.barcode_number || '',
            linProductName,
            linProductCode: sourceRow.product_code || '',
            linMrp: sourceRow.product_mrp || sourceRow.mrp || '',
            linOpr: sourceRow.qty ? `${sourceRow.qty}` : '',
            linQ: 'Q:08', linPacker: 'Packer:09',
            linSizeWidth: '', linSizeHeight: '',
            linCoverage: '', linCoverageUnit: 'Sqft', linPieces: sourceRow.qty ? `${sourceRow.qty}` : '',
            linNetWeight: '', linGrossWeight: '', linBarcode: sourceRow.barcode_number || '',
          };

          if (sourceRow.product_code) {
            try {
              const res = await fetchPrinterSettings({
                token: sessionStorage.getItem("access_token"),
                tenant_id: tenantId,
                product_code: sourceRow.product_code
              }).unwrap();
              if (res?.body?.printer_settings) {
                defaultSettings = { ...defaultSettings, ...res.body.printer_settings };
                if (res.body.printer_settings.templateType) {
                  setSelectedTemplate(res.body.printer_settings.templateType);
                }
                if (res.body.printer_settings.batchNo) {
                  setBatchNumber(res.body.printer_settings.batchNo);
                }
              }
            } catch (err) {
              console.log("Error fetching product printer settings:", err);
            }
          }

          setPrintFormData(defaultSettings);
          setShowPrintForm(true);
          return;
        }

        printQrCodeFunc({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          body: body,
        });
      }
      //print only selected
      else {
        // filter out freezed QRs
        const freezedRows = selectedRows.filter((row) => row.is_freezed === true || row.is_freezed === "true");
        const printableRows = selectedRows.filter((row) => !(row.is_freezed === true || row.is_freezed === "true"));

        if (freezedRows.length > 0) {
          toast.error(`${freezedRows.length} selected QR(s) are freezed and cannot be printed.`);
        }

        if (printableRows.length === 0) {
          return; // nothing to print
        }

        const qrIds = printableRows.map((each) => each.id);

        const body = {
          printer: selectedPrinter,
          type: "1",
          qr_ids: qrIds,
          childOrParent: childOrParent,
          batchNumber: batchNumber || undefined,
        };

        // VOX: Show print form first to collect label details before backend call
        if (isVoxUrl) {
          const sourceRow = printableRows[0] || {};
          const rawName = sourceRow.product_name || sourceRow.name || '';
          const linProductName = rawName.replace(/\s+\d[\d.,]*[Mm][xX×]\d+\s*$/, '').trim();
          setPendingPrintBody(body);
          setPrintFormIsBatch(false);
          
          let defaultSettings = {
            pieces: sourceRow.qty ? `${sourceRow.qty}` : '10', rft: '', rftUnit: 'meter',
            lengthVal: '', lengthUnit: 'sqm', length: '',
            qCode: 'Q:08', packerCode: 'Packer:09',
            netWeight: '', grossWeight: '',
            nominalQty: '', nominalWidth: '', nominalHeight: '',
            productCode: sourceRow.product_code || '',
            productColor: sourceRow.color || '',
            productCategoryName: sourceRow.product_category_name || sourceRow.classification || '',
            categoryName: sourceRow.category_name || '',
            mrp: sourceRow.product_mrp || sourceRow.mrp || '',
            barcodeNumber: sourceRow.barcode_number || '',
            linProductName,
            linProductCode: sourceRow.product_code || '',
            linMrp: sourceRow.product_mrp || sourceRow.mrp || '',
            linOpr: sourceRow.qty ? `${sourceRow.qty}` : '',
            linQ: 'Q:08', linPacker: 'Packer:09',
            linSizeWidth: '', linSizeHeight: '',
            linCoverage: '', linCoverageUnit: 'Sqft', linPieces: sourceRow.qty ? `${sourceRow.qty}` : '',
            linNetWeight: '', linGrossWeight: '',
          };

          if (sourceRow.product_code) {
            try {
              const res = await fetchPrinterSettings({
                token: sessionStorage.getItem("access_token"),
                tenant_id: tenantId,
                product_code: sourceRow.product_code
              }).unwrap();
              if (res?.body?.printer_settings) {
                defaultSettings = { ...defaultSettings, ...res.body.printer_settings };
                if (res.body.printer_settings.templateType) {
                  setSelectedTemplate(res.body.printer_settings.templateType);
                }
                if (res.body.printer_settings.batchNo) {
                  setBatchNumber(res.body.printer_settings.batchNo);
                }
              }
            } catch (err) {
              console.log("Error fetching product printer settings:", err);
            }
          }

          setPrintFormData(defaultSettings);
          setShowPrintForm(true);
          return;
        }

        printQrCodeFunc({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          body: body,
        });
      }
    }
  };

  const handleSendToPrinter = async (postData) => {
    console.log("handleSendToPrinter", postData);

    if (!selectedPrinter || !selectedPrinter.local_ip) {
      toast.error("Invalid Local IP", { id: "unknownPrinterType" });
      return;
    }

    // Zeel Tenant check: add parent boolean flag to payload
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const currentTenantId = vendorData.tenant_id;
    const isZeel = currentTenantId == TenantSlugs.zeel || currentTenantId == "52" || currentTenantId == "57";

    let parentFlag = undefined;
    if (isZeel) {
      const sampleLabel = (postData?.labels && postData.labels[0]) || (postData?.qrcodes && postData.qrcodes[0]);
      let rawType = sampleLabel?.qr_type ?? postData?.qr_type ?? postData?.qrType;

      // Fallback to component state selectedRows or table data if not inside postData
      const sampleRow = (selectedRows && selectedRows.length > 0 && selectedRows[0]?.qr_type ? selectedRows[0] : null) || (data && data.length > 0 ? data[0] : null);
      if (rawType === undefined && sampleRow) {
        rawType = sampleRow.qr_type;
      }

      if (rawType == 2 || rawType == "2") {
        parentFlag = true;
      } else if (rawType == 1 || rawType == "1") {
        parentFlag = false;
      } else if (childOrParent === "parent" || postData?.childOrParent === "parent") {
        parentFlag = true;
      } else if (childOrParent === "child" || postData?.childOrParent === "child") {
        parentFlag = false;
      }
    }

    // Merge user-entered form values (colon-prefixed) into the backend variables
    const formSnap = voxFormDataRef.current;
    const hasForm = Object.keys(formSnap).length > 0;
    const payload = {
      ...postData,
      ...(parentFlag !== undefined ? { parent: parentFlag } : {}),
      variables: {
        ...(postData.variables || {}),
        ...(hasForm ? formSnap : {}),
      },
    };

    try {
      const response = await axios.post(
        `${selectedPrinter.local_ip}/index.php`,
        payload
      );
      console.log("Response:", response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // ========== LABEL PRINTING FUNCTIONS ==========
  const handlePrintSingleLabel = async (row) => {
    if (!row || !row.unique_code) {
      toast.error("Invalid data for label printing");
      return;
    }

    // VOX: Show print form first to collect label details
    if (isVoxUrl) {

      setPrintFormRow(row);
      setPrintFormIsBatch(false);
      
      let defaultSettings = {
        pieces: row.qty ? `${row.qty}` : '10',
        rft: '',
        rftUnit: 'meter',
        lengthVal: '',
        lengthUnit: 'sqm',
        length: '',
        qCode: 'Q:08',
        packerCode: 'Packer:09',
        nominalQty: '', nominalWidth: '', nominalHeight: '', netWeight: '', grossWeight: '',
        barcodeNumber: row.barcode_number || ''
      };

      if (row.product_code) {
        try {
          const res = await fetchPrinterSettings({
            token: sessionStorage.getItem("access_token"),
            tenant_id: tenantId,
            product_code: row.product_code
          }).unwrap();
          if (res?.body?.printer_settings) {
            defaultSettings = { ...defaultSettings, ...res.body.printer_settings };
            if (res.body.printer_settings.templateType) {
              setSelectedTemplate(res.body.printer_settings.templateType);
            }
            if (res.body.printer_settings.batchNo) {
              setBatchNumber(res.body.printer_settings.batchNo);
            }
          }
        } catch (err) {
          console.log("Error fetching product printer settings:", err);
        }
      }

      setPrintFormData(defaultSettings);
      setShowPrintForm(true);
      return;
    }

    setPrintLabelLoading(true);
    setCurrentPrintingId(row.id);

    try {
      // Build complete label data
      const labelData = buildLabelData(row, batchNumber ? { batchNo: batchNumber } : {});
      console.log("Sending label data to printer:", labelData);

      const response = await axios.post(
        LABEL_PRINTER_CONFIG.phpEndpoint,
        labelData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: LABEL_PRINTER_CONFIG.timeout,
        }
      );

      const result = response.data;

      if (result.status === "success") {
        toast.success(`Label printed for ${row.batch_running_code}`);
      } else {
        toast.error(`Label printing failed: ${result.message || "Unknown error"}`);
      }

    } catch (error) {
      console.error("Label printing error:", error);

      if (error.response) {
        toast.error(`Server error: ${error.response.status} - ${error.response.data.error || 'Unknown error'}`);
      } else if (error.request) {
        toast.error("No response from server. Check PHP server is running.");
      } else {
        toast.error(`Request error: ${error.message}`);
      }
    } finally {
      setPrintLabelLoading(false);
      setCurrentPrintingId(null);
    }
  };

  const handleBatchPrintLabels = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one item to print labels");
      return;
    }

    // Filter out freezed rows
    const printableRows = selectedRows.filter(row =>
      !(row.is_freezed === true || row.is_freezed === "true") && !row.type
    );

    if (printableRows.length === 0) {
      toast.error("No printable items selected");
      return;
    }

    if (printableRows.length > 20) {
      if (!window.confirm(`You are about to print ${printableRows.length} labels. This may take a while. Continue?`)) {
        return;
      }
    }

    setBatchLabelPrinting(true);
    let successCount = 0;
    let errorCount = 0;

    // Process each row
    for (let i = 0; i < printableRows.length; i++) {
      const row = printableRows[i];
      try {
        // Build complete label data for this row
        const labelData = buildLabelData(row, batchNumber ? { batchNo: batchNumber } : {});

        const response = await axios.post(
          LABEL_PRINTER_CONFIG.phpEndpoint,
          labelData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: LABEL_PRINTER_CONFIG.timeout,
          }
        );

        const result = response.data;

        if (result.status === "success") {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to print label for ${row.unique_code}:`, result.message);
        }

        // Small delay between prints to prevent printer overload
        if (i < printableRows.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

      } catch (error) {
        errorCount++;
        console.error(`Error printing label for ${row.unique_code}:`, error);
      }
    }

    setBatchLabelPrinting(false);

    if (errorCount === 0) {
      toast.success(`Successfully printed ${successCount} labels`);
    } else {
      // toast.error(`Printed ${successCount} labels, ${errorCount} failed`);
    }
  };

  const handleOpenLabelPrintModal = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select items to print labels");
      return;
    }

    const printableRows = selectedRows.filter(row =>
      !(row.is_freezed === true || row.is_freezed === "true") && !row.type
    );

    if (printableRows.length === 0) {
      toast.error("No printable items selected");
      return;
    }

    setSelectedForLabelPrint(printableRows);

    // VOX: Show print form first to collect label details
    if (isVoxUrl) {
      const sourceRow = printableRows[0] || {};
      const rawName = sourceRow.product_name || sourceRow.name || '';
      const linProductName = rawName.replace(/\s+\d[\d.,]*[Mm][xX×]\d+\s*$/, '').trim();
      setPrintFormIsBatch(true);
      
      let defaultSettings = {
        pieces: sourceRow.qty ? `${sourceRow.qty}` : '10',
        rft: '', rftUnit: 'meter',
        lengthVal: '', lengthUnit: 'sqm', length: '',
        qCode: 'Q:08',
        packerCode: 'Packer:09',
        nominalQty: '', nominalWidth: '', nominalHeight: '', netWeight: '', grossWeight: '',
        barcodeNumber: sourceRow.barcode_number || '',
        // Linerio pre-population
        linProductName,
        linProductCode: sourceRow.product_code || '',
        linMrp: sourceRow.product_mrp || sourceRow.mrp || '',
        linOpr: sourceRow.qty ? `${sourceRow.qty}` : '',
        linQ: 'Q:08', linPacker: 'Packer:09',
        linSizeWidth: '', linSizeHeight: '',
        linCoverage: '', linCoverageUnit: 'Sqft', linPieces: sourceRow.qty ? `${sourceRow.qty}` : '',
        linNetWeight: '', linGrossWeight: '', linBarcode: sourceRow.barcode_number || '',
      };

      if (sourceRow.product_code) {
        try {
          const res = await fetchPrinterSettings({
            token: sessionStorage.getItem("access_token"),
            tenant_id: tenantId,
            product_code: sourceRow.product_code
          }).unwrap();
          if (res?.body?.printer_settings) {
            defaultSettings = { ...defaultSettings, ...res.body.printer_settings };
            if (res.body.printer_settings.templateType) {
              setSelectedTemplate(res.body.printer_settings.templateType);
            }
            if (res.body.printer_settings.batchNo) {
              setBatchNumber(res.body.printer_settings.batchNo);
            }
          }
        } catch (err) {
          console.log("Error fetching product printer settings:", err);
        }
      }

      setPrintFormData(defaultSettings);
      setShowPrintForm(true);
      return;
    }

    setLabelPrintModal(true);
  };

  // ========== GREEN BUTTON: SEND XLSX TO LOCALHOST:8000 ==========
  const handleGreenButtonPrint = async () => {
    const rowsToPrint = selectedRows.filter(row => !row.type);

    if (rowsToPrint.length === 0) {
      toast.error("Please select at least one row to print");
      return;
    }

    // Store rows and try to fetch template by category
    setPendingGreenPrintRows(rowsToPrint);

    // Try to get category_id from the first row's data
    const firstRow = data && data.length > 0 ? data[0] : null;
    const categoryId = firstRow?.category_id || location.state?.category_id;

    if (categoryId) {
      fetchTemplateByCategoryId({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        categoryId: categoryId,
      });
    } else {
      // No category_id available, show modal with all templates
      setSelectedTemplate(null);
      setShowTemplateModal(true);
    }
  };

  // Actually send the XLSX after template + print-details form are filled
  const sendGreenPrintXlsx = async (rowsToPrint, template, formValues = {}) => {
    try {
      toast.loading("Preparing file...", { id: "xlsxSend" });

      // Destructure form values (same value applied to every row)
      const {
        pieces = '',
        length = '',
        rft = '',
        qCode = '',
        packerCode = '',
        batchNo = '',
      } = formValues;

      // Build worksheet data — form columns appended to every row
      const sheetData = rowsToPrint.map((row, idx) => ({
        SNo: idx + 1,
        BatchRunningCode: row.batch_running_code || "",
        ProductCode: row.product_code || "",
        ProductName: row.product_name || "",
        UniqueCode: row.unique_code || "",
        ScratchCode: row.scratch_code || "",
        MRP: row.mrp || "",
        QrStatus: row.qr_status == "2" ? "Scanned" : "Not Scanned",
        IsPrinted: row.is_printed ? "Yes" : "No",
        BatchCode: row.batch_code || "",
        CreatedAt: row.created_at ? new Date(row.created_at).toLocaleString() : "",
        // ── Form values appended to every row ──────────────────────────
        "number of pieces": pieces,
        "length(in mtr.)": length,
        "length in rft": rft ? `${rft} Rft` : '',
        "Q:code": qCode,
        "packer code": packerCode,
        "batch no": batchNo,
      }));

      console.log("============ EXCEL SHEET DATA ============");
      console.log(JSON.stringify(sheetData, null, 2));
      console.log("Total rows:", sheetData.length);
      console.log("Form values appended:", { pieces, length, rft, qCode, packerCode, batchNo });

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "QRCodes");

      // Convert to binary blob
      const xlsxBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([xlsxBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `qr_batch_${location.state?.id || "export"}_${Date.now()}.xlsx`;

      const formData = new FormData();
      formData.append("file", blob, fileName);
      formData.append("file_name", fileName);

      // Include template info and BTW file if selected
      if (template) {
        formData.append("template_name", template.template_name || "");
        formData.append("template_id", template.template_id || "");

        // Convert base64 BTW file to Blob and append + console log
        if (template.btw_file_base64) {
          console.log("============ BTW FILE INFO ============");
          console.log("Template name:", template.template_name);
          console.log("BTW file name:", template.__btw_file_name__ || "template.btw");
          console.log("BTW base64 length (chars):", template.btw_file_base64.length);
          console.log("BTW base64 preview (first 200 chars):", template.btw_file_base64.substring(0, 200));

          const byteCharacters = atob(template.btw_file_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          console.log("BTW file size (bytes):", byteArray.length);
          console.log("BTW first 20 bytes:", Array.from(byteArray.slice(0, 20)));

          const btwBlob = new Blob([byteArray], { type: "application/octet-stream" });
          formData.append("btw_file", btwBlob, template.__btw_file_name__ || "template.btw");
        } else {
          console.log("============ BTW FILE INFO ============");
          console.log("No BTW file attached to template:", template.template_name);
        }
      } else {
        console.log("No template selected — BTW file not sent");
      }

      console.log("============ FORM DATA KEYS BEING SENT ============");
      for (let [key, value] of formData.entries()) {
        if (value instanceof Blob) {
          console.log(key, `[Blob size=${value.size} bytes, type=${value.type}]`);
        } else {
          console.log(key, "=", value);
        }
      }

      const response = await axios.post("http://localhost:8000", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });

      toast.success("File sent to printer server successfully", { id: "xlsxSend" });
      console.log("Printer server response:", response.data);
    } catch (error) {
      console.error("Error sending xlsx:", error);
      if (error.code === "ERR_NETWORK" || error.code === "ECONNREFUSED") {
        toast.error("Cannot reach localhost:8000. Is the printer server running?", { id: "xlsxSend" });
      } else {
        toast.error(`Failed to send file: ${error.message}`, { id: "xlsxSend" });
      }
    }
  };

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full ">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Qr Code Print Batch
            <LinksComponent />
          </div>

          <div className="p-3">
            <h4 className="clear-both  text-right mb-2">
              <button
                onClick={() => generateQRCodePDF()}
                className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] "
              >
                Download pdf
              </button>
            </h4>
            <div className=" bg-white shadow rounded-lg">
              <div className="border-b  border-gray-300 bg-sky-50 rounded-t-md p-3">
                <div className="flex justify-between gap-2">
                  <h4>
                    <span className="text-gray-400 text-sm  block">
                      Generation Id :
                    </span>
                    <span className="flex text-black text-sm  gap-1 ">
                      {location.state.id}
                    </span>
                  </h4>

                  <h4>
                    <span className="text-gray-400 text-sm  block">
                      Batch Code :
                    </span>
                    <span className="flex text-black text-sm  gap-1">
                      {location.state.batch_code}
                    </span>
                  </h4>

                  <h4>
                    <span className="text-gray-400 text-sm  block">
                      Created By :
                    </span>
                    <span className="flex text-black text-sm  gap-1">
                      {location.state.created_by_name}
                    </span>
                  </h4>
                  <h4>
                    <span className="text-gray-400 text-sm  block">
                      Created At :
                    </span>
                    <span className="flex text-black text-sm  gap-1 ">
                      {location.state.created_at.slice(0, 10)}
                    </span>
                  </h4>

                  <h4>
                    <span className="text-gray-400 text-sm  block">
                      Total Qty :
                    </span>
                    <span className="flex text-black text-sm  gap-1">
                      {" "}
                      {location.state.qty}{" "}
                    </span>
                  </h4>
                </div>

                <div className="w-full flex justify-start">
                  <div className="mt-2 w-1/6">
                    <h4>
                      <span className="text-gray-400 text-sm block">
                        Print All In Batch
                      </span>
                      <span className="flex text-black text-sm  gap-1 ">
                        <input
                          type="checkbox"
                          className=""
                          checked={printAllCheckBox}
                          onChange={() => handlePrintAll()}
                        />
                      </span>
                    </h4>
                  </div>

                  <div className="mt-2 w-1/6">
                    <h4>
                      <span className="text-gray-400 text-sm block">All</span>
                      <span className="flex text-black text-sm  gap-1 ">
                        <input
                          type="checkbox"
                          className=""
                          checked={childOrParent === "all" ? true : false}
                          onChange={() => setChildOrParent("all")}
                        />
                      </span>
                    </h4>
                  </div>
                  <div className="mt-2 w-1/6">
                    <h4>
                      <span className="text-gray-400 text-sm block">
                        Only Child
                      </span>
                      <span className="flex text-black text-sm  gap-1 ">
                        <input
                          type="checkbox"
                          className=""
                          checked={childOrParent === "child" ? true : false}
                          onChange={() => setChildOrParent("child")}
                        />
                      </span>
                    </h4>
                  </div>

                  <div className="mt-2 w-1/6">
                    <h4>
                      <span className="text-gray-400 text-sm block">
                        Only Parent
                      </span>
                      <span className="flex text-black text-sm  gap-1 ">
                        <input
                          type="checkbox"
                          className=""
                          checked={childOrParent === "parent" ? true : false}
                          onChange={() => setChildOrParent("parent")}
                        />
                      </span>
                    </h4>
                  </div>
                  <div className="mt-2 mr-4 w-1/8">
                    <h4>
                      <InputComponent
                        name={"StartBatchRunningCode"}
                        type={"text"}
                        value={startBatchRunningCode}
                        setValue={setStartBatchRunningCode}
                        placeholder={"Enter Start Batch Running Code"}
                      />
                    </h4>
                  </div>

                  <div className="mt-2 w-1/6">
                    <h4>
                      <InputComponent
                        name={"EndBatchRunningCode"}
                        type={"text"}
                        value={endBatchRunningCode}
                        setValue={setEndBatchRunningCode}
                        placeholder={"Enter End Batch Running Code"}
                      />
                    </h4>
                  </div>
                </div>
              </div>

              <DataTableComponent
                keyField={(row) => row.SNo}
                columns={columns}
                data={data}
                loading={loading}
                totalRows={totalRows}
                paginationPerPage={paginationPerPage}
                handlePerRowsChange={handlePerRowsChange}
                handlePageChange={handlePageChange}
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={paginationResetToggle}
                selectableRows={true}
                onSelectedRowsChange={handleRowSelected}
                selectableRowDisabled={(row) => row.is_freezed === true || row.is_freezed === "true"}
                selectableRowsComponent={(props) => (
                  <div title={(props?.row?.is_freezed === true || props?.row?.is_freezed === "true") ? "This QR is freezed and cannot be printed" : ""}>
                    <input type="checkbox" {...props} />
                  </div>
                )}
              ></DataTableComponent>

              {/* ========== FLOATING ACTION BUTTONS ========== */}
              {selectedRows.length > 0 && (
                <>
                  {/* QR Code Print Button */}
                  <div
                    className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer"
                    id="box_btn"
                  >
                    <div className="text-right">
                      <Menu className="relative inline-block text-left">
                        <div>
                          <Menu.Button
                            onClick={() => setShowPrinterModal(true)}
                            className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 "
                            style={{ backgroundColor: `${primaryColor["700"]}` }}
                          >
                            <HiPrinter className="w-full h-full p-3" />
                          </Menu.Button>
                        </div>
                      </Menu>
                    </div>
                  </div>

                  {/* Label Print Button */}
                  <div
                    className="fixed bottom-0 right-32 w-16 h-16 mr-10 mb-2 cursor-pointer"
                    id="label_print_btn"
                  >
                    <div className="text-right">
                      <button
                        onClick={handleGreenButtonPrint}
                        className="inline-flex justify-center w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 bg-green-600"
                        title="Send selected rows as XLSX to printer server"
                      >
                        <FaPrint className="w-full h-full p-3" />
                      </button>
                    </div>
                  </div>
                </>
              )}

          {/* ========== TEMPLATE SELECTION MODAL (Green Button) ========== */}
          {showTemplateModal && (
            <ModalComponent
              size={"md"}
              data={null}
              show={showTemplateModal}
              setShow={setShowTemplateModal}
            >
              <div className="w-full p-4">
                <h2 className="text-lg font-semibold mb-4">Select Printer Template</h2>

                {selectedTemplate && (
                  <div className="mb-4 p-3 bg-green-50 rounded border border-green-200">
                    <p className="text-sm text-green-800 font-medium">Matched Template for Category:</p>
                    <p className="text-base font-bold text-green-900 mt-1">{selectedTemplate.template_name}</p>
                    {selectedTemplate.preview_image && (
                      <img
                        src={selectedTemplate.preview_image}
                        alt="Template Preview"
                        className="mt-2 w-40 h-40 object-cover rounded border"
                      />
                    )}
                  </div>
                )}

                {!selectedTemplate && (
                  <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                    <p className="text-sm text-yellow-800">No template mapped for this category. Please select one below.</p>
                  </div>
                )}

                {allTemplates.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Choose a different template:</label>
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {allTemplates.map((tmpl, index) => (
                        <li
                          key={index}
                          onClick={() => setSelectedTemplate(tmpl)}
                          className={`border rounded-lg p-2 flex items-center justify-between cursor-pointer transition-transform transform hover:scale-105 ${
                            selectedTemplate?.id === tmpl.id
                              ? "bg-blue-50 border-blue-400"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {tmpl.preview_image && (
                              <img
                                src={tmpl.preview_image}
                                alt="preview"
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <div>
                              <h3 className="text-sm font-medium">{tmpl.template_name}</h3>
                              <p className="text-xs text-gray-500">{tmpl.__btw_file_name__ || `ID: ${tmpl.template_id}`}</p>
                            </div>
                          </div>
                          <div className="rounded-full w-5 h-5 border border-gray-300 flex items-center justify-center">
                            {selectedTemplate?.id === tmpl.id && (
                              <div className="bg-blue-500 w-3 h-3 rounded-full"></div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setShowTemplateModal(false);
                      setSelectedTemplate(null);
                      setPendingGreenPrintRows([]);
                    }}
                    className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowTemplateModal(false);
                      // Store template and open the print-details form
                      setPendingGreenTemplate(selectedTemplate);
                      setGreenPrintFormData({
                        pieces: '',
                        length: '',
                        rft: '',
                        qCode: '',
                        packerCode: '',
                        batchNo: '',
                      });
                      setShowGreenPrintForm(true);
                    }}
                    className="bg-green-600 hover:bg-green-500 rounded-sm text-white font-bold py-2 px-4 flex items-center"
                  >
                    <FaPrint className="mr-2" />
                    Next: Fill Details
                  </button>
                </div>
              </div>
            </ModalComponent>
          )}

          {/* ========== GREEN PRINT DETAILS FORM MODAL ========== */}
          {showGreenPrintForm && (
            <ModalComponent size={"md"} data={null} show={showGreenPrintForm} setShow={setShowGreenPrintForm}>
              <div className="w-full p-4">
                <h2 className="text-lg font-semibold mb-1">Print Details</h2>
                <p className="text-sm text-gray-500 mb-4">
                  These values will be appended as extra columns to every row in the Excel file.
                </p>

                <div className="space-y-3">
                  {/* Number of pieces */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Pieces
                    </label>
                    <input
                      type="number"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="e.g. 10"
                      value={greenPrintFormData.pieces}
                      onChange={(e) =>
                        setGreenPrintFormData({ ...greenPrintFormData, pieces: e.target.value })
                      }
                    />
                  </div>

                  {/* Length in metres */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Length (in Mtr.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="e.g. 30.50"
                      value={greenPrintFormData.length}
                      onChange={(e) => {
                        const meters = parseFloat(e.target.value) || 0;
                        const feet = meters > 0 ? (meters * 3.28084).toFixed(3) : '';
                        setGreenPrintFormData({
                          ...greenPrintFormData,
                          length: e.target.value,
                          rft: feet,
                        });
                      }}
                    />
                  </div>

                  {/* RFT – read-only, auto-converted */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Length in RFT&nbsp;
                      <span className="text-xs text-gray-400">(auto-converted from metres)</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded p-2 text-sm bg-gray-50 text-gray-500"
                      value={greenPrintFormData.rft ? `${greenPrintFormData.rft} Rft` : ''}
                      readOnly
                      placeholder="Auto-calculated"
                    />
                  </div>

                  {/* Q Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Q:code
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="e.g. Q:08"
                      value={greenPrintFormData.qCode}
                      onChange={(e) =>
                        setGreenPrintFormData({ ...greenPrintFormData, qCode: e.target.value })
                      }
                    />
                  </div>

                  {/* Packer Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Packer Code
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="e.g. Packer:09"
                      value={greenPrintFormData.packerCode}
                      onChange={(e) =>
                        setGreenPrintFormData({ ...greenPrintFormData, packerCode: e.target.value })
                      }
                    />
                  </div>

                  {/* Batch No */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch No
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="e.g. 4N/PT WE01"
                      value={greenPrintFormData.batchNo}
                      onChange={(e) =>
                        setGreenPrintFormData({ ...greenPrintFormData, batchNo: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowGreenPrintForm(false);
                      setPendingGreenPrintRows([]);
                      setPendingGreenTemplate(null);
                    }}
                    className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const rows = pendingGreenPrintRows;
                      const tmpl = pendingGreenTemplate;
                      const formVals = { ...greenPrintFormData };
                      setShowGreenPrintForm(false);
                      setPendingGreenPrintRows([]);
                      setPendingGreenTemplate(null);
                      sendGreenPrintXlsx(rows, tmpl, formVals);
                    }}
                    className="bg-green-600 hover:bg-green-500 rounded-sm text-white font-bold py-2 px-4 flex items-center"
                  >
                    <FaPrint className="mr-2" />
                    Send to Printer
                  </button>
                </div>
              </div>
            </ModalComponent>
          )}
            </div>
          </div>

          {/* ========== QR CODE PRINTER MODAL ========== */}
          {showPrinterModal && (
            <ModalComponent
              size={"md"}
              data={"nadnandand"}
              show={showPrinterModal}
              setShow={setShowPrinterModal}
            >
              <div className="w-full p-3">
                <div className="container mx-auto p-4">
                  <h1 className="text-lg font-semibold mb-4">Choose Printer</h1>

                  <ul className="space-y-2">
                    {printerListData &&
                      printerListData.body &&
                      printerListData.body.data.map((printer, index) => (
                        <li
                          key={index}
                          onClick={() => setSelectedPrinter(printer)}
                          className="bg-white border rounded-lg p-2 flex items-center justify-between transition-transform transform hover:scale-105"
                        >
                          <div>
                            <h2 className="text-base mb-1">{printer.name}</h2>
                          </div>
                          <div className="rounded-full w-6 h-6 border border-gray-300 flex items-center justify-center">
                            {selectedPrinter?.id === printer.id && (
                              <div className="bg-blue-500 w-3 h-3 rounded-full"></div>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>

                  {isVoxUrl && (!data || !data[0]?.tempelate_name) && (
                    <div className="mt-6 border-t pt-4">
                      <h2 className="text-base font-semibold mb-3">Choose Template</h2>
                      <select
                        className="w-full border rounded p-2 text-sm"
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                      >
                        <option value="vox_label.prn">vox_label.prn</option>
                        <option value="linerio_label.prn">linerio_label.prn</option>
                        <option value="volos_label.prn">volos_label.prn</option>
                        <option value="spc_flooring_primerra.prn">spc_flooring_primerra.prn</option>
                        <option value="volos_label_standing.prn">volos_label_standing.prn</option>
                      </select>
                    </div>
                  )}

                  {isVoxUrl && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                      <input
                        type="text"
                        className="w-full border rounded p-2 text-sm"
                        placeholder="Enter Batch Number"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => handleSubmit()}
                      disabled={
                        selectedPrinter == null || printQrCodeisLoading == true
                          ? true
                          : false
                      }
                      className="bg-blue-600 disabled:bg-gray-500 hover:bg-blue-500 rounded-sm text-white font-bold py-2 px-4 mr-2"
                    >
                      <HiPrinter className="mr-2 inline" />
                      Print
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPrinter(null);
                        setShowPrinterModal(false);
                      }}
                      className="bg-red-700 hover:bg-red-600 rounded-sm text-white font-bold py-2 px-4"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </ModalComponent>
          )}

          {/* ========== LABEL PRINT CONFIRMATION MODAL ========== */}
          {labelPrintModal && (
            <ModalComponent
              size={"md"}
              data={null}
              show={labelPrintModal}
              setShow={setLabelPrintModal}
            >
              <div className="w-full p-4">
                <h2 className="text-lg font-semibold mb-4">Print Product Labels</h2>

                <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This will print product labels to the label printer.
                  </p>
                  <p className="text-sm text-yellow-800 mt-1">
                    PHP Endpoint: <code>{LABEL_PRINTER_CONFIG.phpEndpoint}</code>
                  </p>
                </div>

                <div className="mb-4">
                  <p className="text-gray-700">
                    You are about to print <strong>{selectedForLabelPrint.length}</strong> product label(s).
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Selected items:
                  </p>
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2">
                    {selectedForLabelPrint.slice(0, 10).map((row, index) => (
                      <div key={index} className="text-sm text-gray-700 py-1 border-b last:border-0">
                        {row.batch_running_code} - {row.unique_code}
                      </div>
                    ))}
                    {selectedForLabelPrint.length > 10 && (
                      <div className="text-sm text-gray-500 py-1">
                        ... and {selectedForLabelPrint.length - 10} more
                      </div>
                    )}
                  </div>
                </div>

                {isVoxUrl && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                    <input
                      type="text"
                      className="w-full border rounded p-2 text-sm"
                      placeholder="Enter Batch Number"
                      value={batchNumber}
                      onChange={(e) => setBatchNumber(e.target.value)}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setLabelPrintModal(false);
                      setSelectedForLabelPrint([]);
                    }}
                    className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4"
                    disabled={batchLabelPrinting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBatchPrintLabels}
                    className="bg-green-600 hover:bg-green-500 rounded-sm text-white font-bold py-2 px-4 flex items-center"
                    disabled={batchLabelPrinting}
                  >
                    {batchLabelPrinting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Printing...
                      </>
                    ) : (
                      <>
                        <FaPrint className="mr-2" />
                        Print Labels
                      </>
                    )}
                  </button>
                </div>
              </div>
            </ModalComponent>
          )}

          {/* ========== VOX PRINT FORM MODAL ========== */}
          {showPrintForm && (
            <ModalComponent size={"md"} data={null} show={showPrintForm} setShow={setShowPrintForm}>
              <div className="w-full p-4">
                <h2 className="text-lg font-semibold mb-4">Label Print Details</h2>
                <div className="space-y-3">
                  {selectedTemplate === "linerio_label.prn" ? (
                    /* ── Fields for linerio_label.prn ── */
                    <>
                      {/* Product Name (dimension-stripped) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. SP LINERIO M-LINE NATURAL"
                          value={printFormData.linProductName || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, linProductName: e.target.value })} />
                        <p className="text-xs text-gray-400 mt-0.5">Dimension suffix (e.g. 3,05Mx16) is auto-stripped</p>
                      </div>
                      {/* Product Code & MRP */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Product Code</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 3031362"
                            value={printFormData.linProductCode || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linProductCode: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">MRP (₹ / box)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 18392.27"
                            value={printFormData.linMrp || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linMrp: e.target.value })} />
                        </div>
                      </div>
                      {/* OPR / Q / Packer */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">OPR (Qty/Box)</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 01"
                            value={printFormData.linOpr || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linOpr: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Q Code (number only)</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 17"
                            value={printFormData.linQ || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linQ: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Packer (number only)</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 10"
                            value={printFormData.linPacker || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linPacker: e.target.value })} />
                        </div>
                      </div>
                      {/* Size (width × height mm) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Size (mm)</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <input type="number" className="w-full border rounded p-2 text-sm" placeholder="length e.g. 3050"
                              value={printFormData.linSizeWidth || ''}
                              onChange={(e) => setPrintFormData({ ...printFormData, linSizeWidth: e.target.value })} />
                            <p className="text-xs text-gray-400 mt-0.5">length (mm)</p>
                          </div>
                          <div>
                            <input type="number" className="w-full border rounded p-2 text-sm" placeholder="width e.g. 122"
                              value={printFormData.linSizeHeight || ''}
                              onChange={(e) => setPrintFormData({ ...printFormData, linSizeHeight: e.target.value })} />
                            <p className="text-xs text-gray-400 mt-0.5">width (mm)</p>
                          </div>
                        </div>
                        {printFormData.linSizeWidth && printFormData.linSizeHeight && (
                          <p className="text-xs text-blue-600 mt-1">Preview: {printFormData.linSizeWidth}×{printFormData.linSizeHeight} mm</p>
                        )}
                      </div>
                      {/* Coverage & Pieces */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Coverage (/box)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 64.08"
                            value={printFormData.linCoverage || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linCoverage: e.target.value })} />
                          {/* Unit selector */}
                          <div className="flex gap-3 mt-1 flex-wrap">
                            {['m', 'm²', 'Sqft', 'Rft'].map((unit) => (
                              <label key={unit} className="flex items-center gap-1 text-xs font-medium text-gray-600 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  className="accent-blue-600"
                                  checked={printFormData.linCoverageUnit === unit}
                                  onChange={() => setPrintFormData({ ...printFormData, linCoverageUnit: unit })}
                                />
                                {unit}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pieces (pcs/box)</label>
                          <input type="number" className="w-full border rounded p-2 text-sm" placeholder="e.g. 16"
                            value={printFormData.linPieces || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linPieces: e.target.value })} />
                        </div>
                      </div>
                      {/* Net & Gross Weight */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Net Weight (kg)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 26.20"
                            value={printFormData.linNetWeight || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linNetWeight: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gross Weight (kg)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 27.70"
                            value={printFormData.linGrossWeight || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, linGrossWeight: e.target.value })} />
                        </div>
                      </div>
                      {/* Barcode */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Barcode Number</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 5905952337738"
                          value={printFormData.linBarcode || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, linBarcode: e.target.value })} />
                        <p className="text-xs text-gray-400 mt-0.5">13-digit EAN barcode</p>
                      </div>
                    </>
                  ) : (selectedTemplate === "volos_label.prn" || selectedTemplate === "volos_label_standing.prn" || selectedTemplate === "spc_flooring_primerra.prn") ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nominal Qty</label>
                        <input type="number" className="w-full border rounded p-2 text-sm" placeholder="e.g. 10"
                          value={printFormData.nominalQty}
                          onChange={(e) => setPrintFormData({ ...printFormData, nominalQty: e.target.value })} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nominal Width (Mtrs)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="Width"
                            value={printFormData.nominalWidth}
                            onChange={(e) => setPrintFormData({ ...printFormData, nominalWidth: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nominal Height (Mtrs)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="Height"
                            value={printFormData.nominalHeight}
                            onChange={(e) => setPrintFormData({ ...printFormData, nominalHeight: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Net Weight</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="Net Weight"
                            value={printFormData.netWeight}
                            onChange={(e) => setPrintFormData({ ...printFormData, netWeight: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gross Weight</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="Gross Weight"
                            value={printFormData.grossWeight}
                            onChange={(e) => setPrintFormData({ ...printFormData, grossWeight: e.target.value })} />
                        </div>
                      </div>
                    </>
                  ) : selectedTemplate === "vox_label.prn" ? (
                    /* ── Fields actually used in vox_label.prn ── */
                    <>
                      {/* Product Code, Color, Category — pre-populated from product data, editable */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Product Code</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 3031612"
                            value={printFormData.productCode || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, productCode: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Balance Oak"
                            value={printFormData.productColor || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, productColor: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Product Category Name</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. SVP-22"
                            value={printFormData.productCategoryName || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, productCategoryName: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category Name</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Welo SVP-22"
                            value={printFormData.categoryName || ''}
                            onChange={(e) => setPrintFormData({ ...printFormData, categoryName: e.target.value })} />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 590595217222"
                          value={printFormData.barcodeNumber || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, barcodeNumber: e.target.value })} />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">MRP (Rs.)</label>
                        <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 167.00"
                          value={printFormData.mrp || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, mrp: e.target.value })} />
                      </div>

                      {/* Pieces */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Pieces</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 10 NOS"
                          value={printFormData.pieces}
                          onChange={(e) => setPrintFormData({ ...printFormData, pieces: e.target.value })} />
                      </div>

                      {/* RFT with Sqm / Meter toggle → :rft */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Area in Sqm or m</label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="0.01" className="flex-1 border rounded p-2 text-sm"
                            placeholder="Area in Sqm or m"
                            value={printFormData.rft}
                            onChange={(e) => setPrintFormData({ ...printFormData, rft: e.target.value })} />
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap">
                            <input type="checkbox" checked={printFormData.rftUnit === 'sqm'}
                              onChange={() => setPrintFormData({ ...printFormData, rftUnit: 'sqm' })} className="accent-blue-600" />
                            Sqm
                          </label>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap">
                            <input type="checkbox" checked={printFormData.rftUnit === 'meter'}
                              onChange={() => setPrintFormData({ ...printFormData, rftUnit: 'meter' })} className="accent-blue-600" />
                            Meter
                          </label>
                        </div>
                      </div>

                      {/* Area in Rft / Sft with toggle → :length_val */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Area in Rft or Sft</label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="0.01" className="flex-1 border rounded p-2 text-sm"
                            placeholder="Area in Rft or Sft"
                            value={printFormData.lengthVal}
                            onChange={(e) => setPrintFormData({ ...printFormData, lengthVal: e.target.value })} />
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap">
                            <input type="checkbox" checked={printFormData.lengthUnit === 'rft'}
                              onChange={() => setPrintFormData({ ...printFormData, lengthUnit: 'rft' })} className="accent-blue-600" />
                            Rft
                          </label>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 cursor-pointer select-none whitespace-nowrap">
                            <input type="checkbox" checked={printFormData.lengthUnit === 'sft'}
                              onChange={() => setPrintFormData({ ...printFormData, lengthUnit: 'sft' })} className="accent-blue-600" />
                            Sft
                          </label>
                        </div>
                      </div>

                      {/* Plain Length → :length */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
                        <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 3.05"
                          value={printFormData.length}
                          onChange={(e) => setPrintFormData({ ...printFormData, length: e.target.value })} />
                      </div>

                      {/* Net Weight & Gross Weight */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Net Weight (Kg)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 4.22"
                            value={printFormData.netWeight}
                            onChange={(e) => setPrintFormData({ ...printFormData, netWeight: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gross Weight (Kg)</label>
                          <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 4.37"
                            value={printFormData.grossWeight}
                            onChange={(e) => setPrintFormData({ ...printFormData, grossWeight: e.target.value })} />
                        </div>
                      </div>

                      {/* Q Code & Packer Code */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Q Code</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Q:08"
                            value={printFormData.qCode}
                            onChange={(e) => setPrintFormData({ ...printFormData, qCode: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Packer Code</label>
                          <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Packer:09"
                            value={printFormData.packerCode}
                            onChange={(e) => setPrintFormData({ ...printFormData, packerCode: e.target.value })} />
                        </div>
                      </div>
                    </>
                  ) : (
                    /* ── Original form for linerio_label.prn and any other templates ── */
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Pieces</label>
                        <input type="number" className="w-full border rounded p-2 text-sm" placeholder="e.g. 10"
                          value={printFormData.pieces}
                          onChange={(e) => setPrintFormData({ ...printFormData, pieces: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Length (Meters)</label>
                        <input type="number" step="0.01" className="w-full border rounded p-2 text-sm" placeholder="e.g. 30.50"
                          value={printFormData.lengthVal}
                          onChange={(e) => {
                            const meters = parseFloat(e.target.value) || 0;
                            const feet = (meters * 3.28084).toFixed(3);
                            setPrintFormData({ ...printFormData, lengthVal: e.target.value, rft: feet });
                          }} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">RFT (Auto-calculated from meters)</label>
                        <input type="text" className="w-full border rounded p-2 text-sm bg-gray-100"
                          value={printFormData.rft ? `${printFormData.rft} Rft` : ''} readOnly />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Q Code</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Q:08"
                          value={printFormData.qCode}
                          onChange={(e) => setPrintFormData({ ...printFormData, qCode: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Packer Code</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. Packer:09"
                          value={printFormData.packerCode}
                          onChange={(e) => setPrintFormData({ ...printFormData, packerCode: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Batch No</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. VOX99"
                          value={printFormData.batchNo || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, batchNo: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                        <input type="text" className="w-full border rounded p-2 text-sm" placeholder="e.g. 590595217222"
                          value={printFormData.barcodeNumber || ''}
                          onChange={(e) => setPrintFormData({ ...printFormData, barcodeNumber: e.target.value })} />
                      </div>
                    </>
                  )}
                  {isVoxUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                      <input type="text" className="w-full border rounded p-2 text-sm" placeholder="Enter Batch Number"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)} />
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={() => { setShowPrintForm(false); setPrintFormRow(null); setPendingPrintBody(null); }}
                    className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4">Cancel</button>
                  <button
                    onClick={async () => {
                      setShowPrintForm(false);
                      const overrides = { ...printFormData };

                      // VOX PRN flow: send form values to backend, merge into index.php variables
                      if (pendingPrintBody) {
                        const overrides = { ...printFormData };
                        // Build colon-prefixed variable map for index.php
                        const isLinerio = selectedTemplate === 'linerio_label.prn';
                        voxFormDataRef.current = isLinerio ? {
                          // Linerio template variable mapping (matches linerio_label.prn placeholders)
                          ...(overrides.linProductName  ? { ':product_name':   overrides.linProductName }  : {}),
                          ...(overrides.linProductCode  ? { ':product_code':   overrides.linProductCode }  : {}),
                          ...(overrides.linMrp          ? { ':mrp':            overrides.linMrp }          : {}),
                          // OPR: strip any "OPR:" prefix — PRN prints "OPR:" as static label
                          ...(overrides.linOpr          ? { ':opr':            overrides.linOpr.replace(/^opr\s*:\s*/i, '').trim() } : {}),
                          // Q value: strip any "Q:" prefix — PRN prints "Q:" as static label
                          ...(overrides.linQ            ? { ':q_value':        overrides.linQ.replace(/^q\s*:\s*/i, '').trim() }   : {}),
                          // Packer value: strip any "Packer:" prefix — PRN prints "Packer:" as static label
                          ...(overrides.linPacker       ? { ':packer_value':   overrides.linPacker.replace(/^packer\s*:\s*/i, '').trim() } : {}),
                          // Size split into width and height — PRN prints "x" and "mm" as static labels
                          ...(overrides.linSizeWidth    ? { ':size_width':     overrides.linSizeWidth }    : {}),
                          ...(overrides.linSizeHeight   ? { ':size_height':    overrides.linSizeHeight }   : {}),
                          ...(overrides.linCoverage     ? { ':coverage':       overrides.linCoverage }     : {}),
                          ...(overrides.linCoverageUnit ? { ':cvg_unit':       overrides.linCoverageUnit } : { ':cvg_unit': 'Sqft' }),
                          ...(overrides.linPieces       ? { ':pieces':         overrides.linPieces }       : {}),
                          ...(overrides.linNetWeight    ? { ':net_weight':     overrides.linNetWeight }    : {}),
                          ...(overrides.linGrossWeight  ? { ':gross_weight':   overrides.linGrossWeight }  : {}),
                          ...(overrides.linBarcode       ? { ':barcode_number': overrides.linBarcode }      : {}),
                          ...(batchNumber               ? { ':batch_no':       batchNumber }               : {}),
                        } : {
                          // vox_label.prn / other templates
                          ...(overrides.pieces            ? { ':pieces':                  overrides.pieces,
                                                            ':quantity_per_box':          overrides.pieces } : {}),
                          ...(overrides.rft               ? { ':rft':                     overrides.rftUnit ? `${overrides.rft} ${overrides.rftUnit}` : overrides.rft }       : {}),
                          ...(overrides.lengthVal         ? { ':length_val':              overrides.lengthUnit ? `${overrides.lengthVal} ${overrides.lengthUnit}` : overrides.lengthVal } : {}),
                          ...(overrides.length            ? { ':length':                  overrides.length }            : {}),
                          ...(overrides.netWeight         ? { ':net_weight':              overrides.netWeight }         : {}),
                          ...(overrides.grossWeight       ? { ':gross_weight':            overrides.grossWeight }       : {}),
                          ...(overrides.qCode             ? { ':q_code':                  overrides.qCode }             : {}),
                          ...(overrides.packerCode        ? { ':packer_code':             overrides.packerCode }        : {}),
                          ...(overrides.productCode       ? { ':product_code':            overrides.productCode }       : {}),
                          ...(overrides.productColor      ? { ':product_color':           overrides.productColor }      : {}),
                          ...(overrides.productCategoryName ? { ':product_category_name': overrides.productCategoryName } : {}),
                          ...(overrides.categoryName        ? { ':category_name':           overrides.categoryName }        : {}),
                          ...(overrides.mrp                 ? { ':mrp':                     overrides.mrp }                 : {}),
                          ...(overrides.barcodeNumber       ? { ':barcode_number':          overrides.barcodeNumber }       : {}),
                          ...(batchNumber                   ? { ':batch_no':                batchNumber }                   : {}),
                        };
                        const bodyWithForm = {
                          ...pendingPrintBody,
                          voxFormData: voxFormDataRef.current,
                        };
                        setPendingPrintBody(null);
                        printQrCodeFunc({
                          token: sessionStorage.getItem("access_token"),
                          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                          body: bodyWithForm,
                        });
                        setPrintFormRow(null);
                        return;
                      }

                      // Legacy image-based label flow (green button)
                      if (printFormIsBatch) {
                        setBatchLabelPrinting(true);
                        let ok = 0, fail = 0;
                        for (let i = 0; i < selectedForLabelPrint.length; i++) {
                          try {
                            const res = await axios.post(LABEL_PRINTER_CONFIG.phpEndpoint, buildLabelData(selectedForLabelPrint[i], overrides), { headers: { 'Content-Type': 'application/json' }, timeout: LABEL_PRINTER_CONFIG.timeout });
                            if (res.data.status === 'success') ok++; else fail++;
                            if (i < selectedForLabelPrint.length - 1) await new Promise(r => setTimeout(r, 500));
                          } catch { fail++; }
                        }
                        setBatchLabelPrinting(false);
                        if (fail === 0) toast.success(`Printed ${ok} labels`); else toast.error(`Printed ${ok}, ${fail} failed`);
                      } else if (printFormRow) {
                        setPrintLabelLoading(true);
                        setCurrentPrintingId(printFormRow.id);
                        try {
                          const res = await axios.post(LABEL_PRINTER_CONFIG.phpEndpoint, buildLabelData(printFormRow, overrides), { headers: { 'Content-Type': 'application/json' }, timeout: LABEL_PRINTER_CONFIG.timeout });
                          if (res.data.status === 'success') toast.success(`Label printed for ${printFormRow.batch_running_code}`);
                          else toast.error(`Failed: ${res.data.message || 'Unknown error'}`);
                        } catch (err) { toast.error(`Error: ${err.message}`); }
                        finally { setPrintLabelLoading(false); setCurrentPrintingId(null); }
                      }
                      setPrintFormRow(null);
                    }}
                    className="bg-green-600 hover:bg-green-500 rounded-sm text-white font-bold py-2 px-4 flex items-center"
                    disabled={printLabelLoading || batchLabelPrinting}>
                    <FaPrint className="mr-2" /> Print Label
                  </button>
                </div>
              </div>
            </ModalComponent>
          )}

          {/* ========== EXISTING CONFIRM MODAL ========== */}
          {confirmModal.open && (
            <ModalComponent
              size={"sm"}
              data={null}
              show={confirmModal.open}
              setShow={(v) => setConfirmModal({ ...confirmModal, open: v })}
            >
              <div className="w-full p-4">
                {confirmModal.unfreezed === 0 ? (
                  <>
                    <h2 className="text-lg font-semibold mb-2">All QR(s) are freezed</h2>
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => setConfirmModal(confirmInitialData)}
                        className="bg-blue-600 hover:bg-blue-500 rounded-sm text-white font-bold py-2 px-4"
                      >
                        OK
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold mb-2">Confirm Print</h2>
                    <div className="text-sm text-gray-800">
                      <p>Total QR(s): <b>{confirmModal.total}</b></p>
                      <p>Freezed QR(s): <b>{confirmModal.freezed}</b></p>
                      <p>Already Printed QR(s): <b>{confirmModal.printed}</b></p>
                      <p>Printing Now : <b>{confirmModal.printing}</b></p>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={() => setConfirmModal(confirmInitialData)}
                        className="bg-gray-500 hover:bg-gray-400 rounded-sm text-white font-bold py-2 px-4"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const payload = confirmModal.dataToSend;
                          setConfirmModal(confirmInitialData);
                          if (payload) {
                            toast.success("Prinitng " + (Array.isArray(payload) ? payload.length : "") + " Qrs", { id: "printQrCodeData" });

                            // VOX: Use tempelate_name from backend if available, else use selectedTemplate
                            const dataToPrint = { ...payload };
                            if (isVoxUrl) {
                              const dbTemplate = payload?.tempelate_name;
                              dataToPrint.filename = dbTemplate || selectedTemplate;
                            }
                            handleSendToPrinter(dataToPrint);
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-500 rounded-sm text-white font-bold py-2 px-4"
                      >
                        Continue
                      </button>
                    </div>
                  </>
                )}
              </div>
            </ModalComponent>
          )}
        </div>
        {showQr && <ViewQr data={qrData} show={showQr} setShow={setShowQr} />}
        {showBar && (
          <ViewBarcode data={qrData} show={showBar} setShow={setShowBar} />
        )}
      </div>
    </>
  );
};

export default QrByBatch;